import React, { useEffect, useState } from 'react';
import { gsap } from 'gsap';
import Modal from './Modal';
import './Slide6.css';

const Slide6Component = () => {
  const [typedText, setTypedText] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState(null);

  const openModal = (cardType) => {
    setModalContent(getModalContent(cardType));
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const getModalContent = (type) => {
    const contents = {
      nodata: {
        title: 'Zero Collecte de Données',
        icon: 'bi bi-x-circle',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-shield-x"></i> Nous ne voulons pas vos données</h3>
              <p>
                La plupart des entreprises tech vous diront qu'elles "respectent votre vie privée" tout en
                collectant massivement vos données "pour améliorer le service". Chez MarIAne, nous adoptons
                une approche radicalement différente : nous ne collectons tout simplement aucune donnée utilisateur.
                Pas par choix éthique seulement, mais par design technique. Notre architecture rend la collecte
                de données impossible, même si nous le voulions.
              </p>
              <p>
                Cette approche "zero data" signifie qu'il n'y a aucun serveur MarIAne qui enregistre vos recherches,
                aucune base de données qui stocke vos habitudes de navigation, aucun système d'analyse qui profile
                vos intérêts. Vous utilisez MarIAne comme un outil local qui travaille pour vous, pas comme un
                service cloud qui vous transforme en produit.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=800&q=80"
              alt="Protection des données personnelles"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-search"></i> Vos recherches vous appartiennent</h3>
              <p>
                Vos requêtes de recherche révèlent vos pensées, vos questions, vos doutes, vos curiosités.
                Elles peuvent révéler votre état de santé quand vous cherchez des symptômes, votre situation
                financière quand vous comparez des prêts, vos opinions politiques quand vous vous informez
                sur l'actualité. Ces informations intimes ne devraient jamais être collectées, analysées ou
                monétisées par qui que ce soit.
              </p>
              <p>
                Avec MarIAne, vos recherches restent privées par conception. Elles ne sont jamais envoyées à
                nos serveurs, ne sont jamais loguées, ne sont jamais analysées pour vous profiler. Elles
                existent uniquement dans votre navigateur, le temps de la requête, puis disparaissent à jamais.
                C'est votre esprit qui explore, pas une entreprise qui vous surveille.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "La meilleure façon de protéger vos données personnelles n'est pas
              de promettre de les sécuriser. C'est de ne jamais les collecter. Zero data, zero risque,
              zero compromis."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-graph-down"></i> Aucune télémétrie cachée</h3>
              <p>
                Beaucoup de logiciels prétendument "privacy-friendly" collectent quand même de la télémétrie
                "anonyme" pour "améliorer le produit". Ces données soi-disant anonymes peuvent souvent être
                dé-anonymisées, et même quand ce n'est pas le cas, elles révèlent des patterns d'utilisation
                qui vous caractérisent. MarIAne n'envoie absolument aucune télémétrie. Pas de "crash reports",
                pas de "analytics anonymes", pas de "feature usage tracking". Rien. Zéro. Nada.
              </p>
              <p>
                Cette absence totale de télémétrie signifie que nous développons MarIAne différemment.
                Nous comptons sur vos retours volontaires, vos contributions open source, et nos propres tests
                plutôt que sur la surveillance passive de millions d'utilisateurs. C'est plus difficile pour nous,
                mais c'est le bon choix éthique.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&q=80"
              alt="Confidentialité et sécurité"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-house-door"></i> Tout reste chez vous</h3>
              <p>
                Dans votre navigateur, sur votre machine, sous votre contrôle. C'est là que MarIAne vit et travaille.
                Votre historique de recherche, vos conversations avec l'assistant IA, vos préférences de filtrage,
                tout est stocké localement dans votre navigateur. Si vous désinstallez MarIAne ou effacez les données
                du navigateur, tout disparaît complètement et définitivement. Il n'y a aucune "sauvegarde cloud"
                surprise qui garde vos données.
              </p>
            </div>
          </>
        )
      },
      local: {
        title: 'Traitement 100% Local',
        icon: 'bi bi-laptop',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-pc-display"></i> L'IA sur votre machine, pas dans le cloud</h3>
              <p>
                Le traitement local est la pierre angulaire de la philosophie de MarIAne. Tous les modèles
                d'intelligence artificielle s'exécutent directement sur votre appareil, utilisant votre processeur
                et votre mémoire pour analyser vos requêtes, générer des résumés et répondre à vos questions.
                Cette architecture locale garantit qu'aucune de vos données ne transite jamais vers des serveurs
                externes où elles pourraient être interceptées, analysées ou stockées.
              </p>
              <p>
                Cette approche "edge AI" représente un changement de paradigme fondamental. Au lieu du modèle
                cloud traditionnel où vos données sont envoyées à des data centers massifs pour traitement,
                MarIAne apporte l'intelligence là où vous êtes. C'est plus respectueux de la vie privée,
                mais aussi plus rapide (pas de latence réseau), plus fiable (pas de dépendance à une connexion),
                et plus durable (moins de transferts de données signifie moins de consommation énergétique).
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?w=800&q=80"
              alt="Computing local et edge AI"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-clock-history"></i> Votre historique, votre contrôle</h3>
              <p>
                L'historique de navigation et de recherche est l'un des ensembles de données les plus révélateurs
                sur une personne. Il trace votre évolution intellectuelle, vos préoccupations quotidiennes, vos
                projets secrets. MarIAne stocke cet historique uniquement localement, chiffré, dans votre navigateur.
                Vous seul y avez accès. Vous pouvez le consulter, le rechercher, l'exporter ou le supprimer quand
                vous voulez. C'est votre mémoire numérique, elle vous appartient entièrement.
              </p>
              <p>
                Contrairement aux moteurs de recherche traditionnels qui conservent votre historique indéfiniment
                sur leurs serveurs (même après que vous l'ayez "supprimé" côté client), MarIAne n'a tout simplement
                aucun serveur où stocker cet historique. Quand vous effacez votre historique local, il disparaît
                réellement et définitivement. Personne ne peut le récupérer, même avec un mandat judiciaire,
                car il n'existe nulle part ailleurs que sur votre machine.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Le traitement local n'est pas une fonctionnalité, c'est une garantie
              architecturale. Vos données ne peuvent pas fuiter vers le cloud si elles ne quittent jamais
              votre appareil."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-cloud-slash"></i> Aucune synchronisation forcée</h3>
              <p>
                Beaucoup de navigateurs modernes poussent fortement la synchronisation cloud, parfois même
                en l'activant par défaut. Cette synchronisation envoie votre historique, vos mots de passe,
                vos favoris et plus encore vers les serveurs de l'entreprise. MarIAne n'a pas de système de
                synchronisation cloud intégré. Si vous voulez synchroniser entre appareils, c'est votre choix,
                avec vos outils, selon vos conditions. Nous ne vous forcerons jamais à uploader vos données
                quelque part.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80"
              alt="Autonomie et contrôle des données"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-shield-lock-fill"></i> Vos données sont vraiment vôtres</h3>
              <p>
                "Vos données vous appartiennent" est devenu un slogan marketing creux répété par des entreprises
                qui en réalité possèdent, analysent et monétisent vos données. Chez MarIAne, cette phrase a un
                sens technique précis : vos données résident physiquement sur votre appareil, vous en avez le
                contrôle total, et nous n'avons littéralement aucun moyen d'y accéder. Possession à 100%,
                pas de petits caractères, pas d'exceptions.
              </p>
            </div>
          </>
        )
      },
      transparency: {
        title: 'Transparence Totale',
        icon: 'bi bi-eye',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-code-square"></i> Code source ouvert et auditable</h3>
              <p>
                La vraie transparence commence par le code source ouvert. MarIAne est un projet open source,
                ce qui signifie que chaque ligne de code, chaque algorithme, chaque fonctionnalité est publique
                et consultable par n'importe qui. Vous n'avez pas à nous faire confiance sur parole quand nous
                disons que nous respectons votre vie privée : vous pouvez vérifier par vous-même ou demander
                à des experts indépendants de le faire pour vous.
              </p>
              <p>
                Cette ouverture du code a plusieurs avantages. D'abord, elle permet des audits de sécurité
                indépendants qui identifient et corrigent les vulnérabilités plus rapidement. Ensuite, elle
                crée une responsabilité publique : nous ne pouvons pas cacher des fonctionnalités de tracking
                ou des backdoors dans le code car elles seraient immédiatement découvertes. Enfin, elle permet
                à la communauté de contribuer, d'améliorer et de personnaliser MarIAne selon ses besoins.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=800&q=80"
              alt="Code open source et transparence"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-list-check"></i> Règles de filtrage transparentes</h3>
              <p>
                Les règles qui déterminent ce qui est bloqué et ce qui est autorisé ne sont pas un secret
                commercial obscur. Elles sont entièrement consultables et modifiables. Vous pouvez voir
                exactement pourquoi un domaine spécifique est bloqué, quelle règle s'applique, et même
                contribuer vos propres règles à la communauté. Cette transparence des filtres vous permet
                de comprendre et de contrôler véritablement votre navigation.
              </p>
              <p>
                Les listes de blocage sont régulièrement mises à jour, mais ces mises à jour sont publiques
                et versionnées. Vous pouvez voir exactement ce qui a changé entre deux versions, pourquoi
                ces changements ont été faits, et même revenir en arrière si vous n'êtes pas d'accord.
                C'est votre navigateur, vous devriez avoir le dernier mot sur ce qui est filtré.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "La transparence n'est pas montrer ce qui est joli et cacher
              le reste. C'est ouvrir complètement le capot et dire : voilà comment ça marche, vérifiez
              par vous-même."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-puzzle"></i> Aucun algorithme de boîte noire</h3>
              <p>
                Les algorithmes de recommandation et de classement des géants tech sont des boîtes noires
                mystérieuses. Personne ne sait exactement comment ils fonctionnent, quels biais ils ont,
                quels critères ils utilisent. MarIAne rejette cette opacité. Nos algorithmes de classement
                et de filtrage sont documentés, explicables et compréhensibles. Vous pouvez savoir pourquoi
                un résultat apparaît avant un autre, et si vous n'êtes pas d'accord, vous pouvez ajuster
                les paramètres.
              </p>
              <p>
                Cette explicabilité est particulièrement importante pour l'IA. Les modèles de langage peuvent
                avoir des comportements surprenants, et nous croyons que vous avez le droit de comprendre
                comment ils prennent leurs décisions. Nous documentons les limites de nos modèles, leurs
                biais connus, et leurs cas d'usage recommandés. Pas de magie marketing, juste de la technologie
                honnête.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80"
              alt="Transparence algorithmique"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-journal-text"></i> Documentation complète et accessible</h3>
              <p>
                Nous documentons tout : comment MarIAne fonctionne techniquement, quelles décisions de design
                nous avons prises et pourquoi, quelles sont nos limites actuelles, notre roadmap future.
                Cette documentation n'est pas un jargon technique réservé aux développeurs, elle est écrite
                pour être accessible à tous. Si vous voulez comprendre en profondeur ce que fait MarIAne
                et comment, toutes les ressources sont disponibles publiquement.
              </p>
            </div>
          </>
        )
      },
      control: {
        title: 'Contrôle Utilisateur',
        icon: 'bi bi-gear',
        content: (
          <>
            <div className="modal-section">
              <h3><i className="bi bi-sliders"></i> Vous décidez de tout</h3>
              <p>
                Le contrôle utilisateur chez MarIAne n'est pas un menu de paramètres basiques avec trois options.
                C'est un contrôle granulaire et complet sur chaque aspect de votre expérience de navigation.
                Vous décidez ce qui est bloqué et ce qui est autorisé, site par site, domaine par domaine,
                voire requête par requête si vous le souhaitez. Aucune décision n'est prise pour vous sans
                votre consentement explicite.
              </p>
              <p>
                Cette philosophie de contrôle total signifie que MarIAne peut être configuré de manière
                radicalement différente par différents utilisateurs selon leurs besoins et valeurs. Un utilisateur
                paranoïaque peut bloquer absolument tout par défaut. Un utilisateur pragmatique peut autoriser
                certains trackers qu'il juge acceptables. Un développeur peut désactiver complètement le filtrage
                pour débugger. C'est votre outil, configurez-le exactement comme vous voulez.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=800&q=80"
              alt="Contrôle et personnalisation"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-geo-alt"></i> Configuration granulaire par site</h3>
              <p>
                Tous les sites web ne sont pas égaux. Certains requièrent plus de JavaScript pour fonctionner,
                d'autres sont de véritables nids à trackers. MarIAne vous permet de définir des règles spécifiques
                pour chaque site individuellement. Vous pouvez avoir un profil de sécurité strict par défaut,
                mais autoriser certains scripts sur votre site bancaire. Vous pouvez bloquer tous les cookies
                tiers sauf sur un site spécifique où vous voulez rester connecté.
              </p>
              <p>
                Cette granularité s'étend aussi aux types de contenu. Vous pouvez autoriser les images mais
                bloquer les vidéos pour économiser de la bande passante. Vous pouvez permettre le JavaScript
                first-party mais bloquer tous les scripts tiers. Vous pouvez même créer des règles conditionnelles
                complexes : autoriser X seulement si Y est présent. Les possibilités sont infinies.
              </p>
            </div>

            <div className="modal-highlight">
              <i className="bi bi-quote"></i> "Le vrai contrôle utilisateur n'est pas de choisir entre
              'accepter' et 'refuser'. C'est d'avoir le pouvoir de configurer chaque aspect de votre
              expérience selon vos valeurs personnelles."
            </div>

            <div className="modal-section">
              <h3><i className="bi bi-download"></i> Export de vos données à tout moment</h3>
              <p>
                Vos données vous appartiennent signifie aussi que vous pouvez les exporter quand vous voulez,
                dans des formats standards et ouverts. Votre historique de recherche, vos paramètres personnalisés,
                vos règles de filtrage, tout peut être exporté en JSON ou CSV. Vous pouvez analyser ces données
                avec vos propres outils, les archiver pour votre usage personnel, ou même les transférer vers
                un autre navigateur ou une autre instance de MarIAne.
              </p>
              <p>
                Cette portabilité des données vous libère du lock-in. Vous n'êtes pas prisonnier de MarIAne.
                Si un jour vous décidez de passer à autre chose, vous emportez toutes vos données avec vous.
                Nous construisons un outil que vous pouvez quitter librement, pas une cage dorée dont vous
                ne pouvez plus sortir.
              </p>
            </div>

            <img
              src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
              alt="Export et portabilité des données"
              className="modal-image"
            />

            <div className="modal-section">
              <h3><i className="bi bi-trash3"></i> Suppression complète et définitive</h3>
              <p>
                Le droit à l'oubli n'est pas qu'un concept légal, c'est une fonctionnalité technique chez MarIAne.
                Quand vous supprimez vos données ou désinstallez MarIAne, tout disparaît complètement et
                définitivement. Il n'y a pas de "copie de sauvegarde" cachée quelque part, pas de serveur qui
                conserve "anonymement" vos données. La suppression est immédiate, locale, et totale. C'est
                votre droit absolu de tout effacer et de recommencer à zéro quand vous voulez.
              </p>
            </div>
          </>
        )
      }
    };

    return contents[type];
  };

  useEffect(() => {
    const tl = gsap.timeline();

    tl.set('.slide6-header', { opacity: 1 });

    tl.add(() => {
      const text = "Qu'est-ce que la navigation éthique ?";
      let index = 0;
      const typeInterval = setInterval(() => {
        if (index <= text.length) {
          setTypedText(text.slice(0, index));
          index++;
        } else {
          clearInterval(typeInterval);
        }
      }, 50);
    }, '+=0.8');

    tl.to('.slide6-search-button', {
      scale: 1.1,
      boxShadow: '0 0 40px rgba(0, 212, 255, 0.8)',
      duration: 0.3,
      repeat: 1,
      yoyo: true
    }, '+=2');

    tl.fromTo('.slide6-main-title',
      { opacity: 0, y: 40 },
      { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
    , '+=0.3');

    tl.fromTo('.slide6-separator',
      { opacity: 0, scaleX: 0 },
      { opacity: 1, scaleX: 1, duration: 0.8, ease: 'power2.inOut' }
    , '-=0.4');

    tl.fromTo('.ethics-card',
      { opacity: 0, y: 50, scale: 0.95 },
      { opacity: 1, y: 0, scale: 1, duration: 0.7, stagger: 0.12, ease: 'power2.out' }
    , '+=0.2');

    return () => tl.kill();
  }, []);

  return (
    <div className="slide-content slide-6-transition">
      <header className="slide6-header">
        <div className="slide6-header-content">
          <div className="slide6-logo-small">
            MAR<span className="neon-text">IA</span>NE
          </div>
          <form className="slide6-search-form">
            <div className="slide6-search-box">
              <input
                type="text"
                className="slide6-search-input"
                placeholder="Rechercher..."
                value={typedText}
                readOnly
              />
              <button type="button" className="slide6-search-button">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
              </button>
            </div>
          </form>
        </div>
      </header>

      <div className="slide6-content">
        <div className="slide6-main-title">
          <h1 className="slide6-h1">L'Éthique au Cœur du Projet</h1>
          <p className="slide6-sub">Notre Engagement Fondamental : L'Éthique D'Abord</p>
        </div>

        <div className="slide6-separator"></div>

        <div className="ethics-grid">
          <div className="ethics-card" onClick={() => openModal('nodata')} style={{ cursor: 'pointer' }}>
            <div className="ethics-icon">
              <i className="bi bi-x-circle"></i>
            </div>
            <h3>Zero Collecte de Données</h3>
            <ul>
              <li>Aucune donnée utilisateur capturée</li>
              <li>Aucun tracking de vos recherches</li>
              <li>Aucune télémétrie vers nos serveurs</li>
              <li>Tout reste local, dans votre navigateur</li>
            </ul>
          </div>

          <div className="ethics-card" onClick={() => openModal('transparency')} style={{ cursor: 'pointer' }}>
            <div className="ethics-icon">
              <i className="bi bi-eye"></i>
            </div>
            <h3>Transparence Totale</h3>
            <ul>
              <li>Code source ouvert</li>
              <li>Règles de filtrage consultables</li>
              <li>Aucun algorithme opaque</li>
              <li>Vous savez exactement ce qui se passe</li>
            </ul>
          </div>

          <div className="ethics-card" onClick={() => openModal('control')} style={{ cursor: 'pointer' }}>
            <div className="ethics-icon">
              <i className="bi bi-gear"></i>
            </div>
            <h3>Contrôle Utilisateur</h3>
            <ul>
              <li>Vous décidez ce qui est bloqué</li>
              <li>Configuration granulaire par site</li>
              <li>Export de vos données à tout moment</li>
              <li>Suppression complète quand vous voulez</li>
            </ul>
          </div>
        </div>
      </div>

      {modalContent && (
        <Modal
          isOpen={modalOpen}
          onClose={closeModal}
          title={modalContent.title}
          icon={modalContent.icon}
          content={modalContent.content}
        />
      )}
    </div>
  );
};

export default Slide6Component;
